import React, { useState, useEffect } from 'react';
import { Drawer, Table, Group, Text, Button, UnstyledButton } from '@mantine/core';
import { IconChevronUp, IconChevronDown } from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import CampaignReport from "../../api/CampaignReport";
import { VisiLoader } from "../../components/GeneralComponent/GeneralComponent";

/* Keyword View for ONE individual Google ad.
   Sibling of ReportKeywordDrawer, which stays exactly as it is: that one shows Google's own
   keyword figures for an ad group. This one shows Visiopt's figures for a single ad, because
   Google reports keywords per ad group only and never per ad — the ad/keyword pair exists only
   on the visits we track ourselves.

   So there is deliberately no Spend, CPC, CTR or Impressions column here. Those are Google's
   numbers and cannot be split across the ads inside an ad group without inventing the split.
   The ad row in the report keeps showing its real total spend. */
export function AdKeywordDrawer({
  selectedAdName,
  selectedAdEncodedId,
  selectedAdCampaignid,
  showAdKeywordDrawer,
  setShowAdKeywordDrawer,
  reportTimeValue,
  attributionName,
  adsTrackingNetwork,
  adsCustomNetwork,
  sourceNetwork,
  testVariationsData,
}) {

  const navigate = useNavigate();
  const [isVisiLoaderVisible, setIsVisiLoaderVisible] = useState(false);
  const [keywordData, setKeywordData] = useState([]);
  const [goals, setGoals] = useState([]);
  const [sortKey, setSortKey] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  useEffect(() => {
    if (showAdKeywordDrawer && selectedAdEncodedId && selectedAdCampaignid) {
      getAdKeywordDrawerData();
    }
  }, [showAdKeywordDrawer, selectedAdEncodedId, selectedAdCampaignid]);

  const getAdKeywordDrawerData = () => {
    setIsVisiLoaderVisible(true);
    CampaignReport.getadkeyworddrawerdata({
      campaign_id: selectedAdCampaignid,
      ad_id: selectedAdEncodedId,
      report_time: reportTimeValue,
      attribution_name: attributionName,
      ads_tracking_network: adsTrackingNetwork,
      ads_custom_network: adsCustomNetwork,
      source: sourceNetwork,
      test_variations_data: testVariationsData,
    })
    .then(response => {
      if (response.ok) return response.json();

      if (response.status === 401 || response.status === 400) {
        navigate('/logout');
      }
    })
    .then(data => {
      if (data?.status === 1) {
        setKeywordData(data.data.keyword_data || []);
        setGoals(data.data.goals || []);
      } else {
        setKeywordData([]);
        setGoals([]);
      }
    })
    .finally(() => {
      setIsVisiLoaderVisible(false);
    });
  };

  /* Goal figures live one level down on each row, so sorting takes a reader that knows how to
     reach them: 'goal:<key>:<metric>' for those, a plain field name for the rest. */
  const readValue = (row, key) => {
    if (key.startsWith('goal:')) {
      const [, goalKey, metric] = key.split(':');
      const value = row.goals?.[goalKey]?.[metric];
      return metric === 'conversions' ? Number(value) || 0 : parseFloat(value) || 0;
    }
    return row[key];
  };

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortKey === key && sortDirection === 'asc') {
      direction = 'desc';
    }
    setSortKey(key);
    setSortDirection(direction);
    const sorted = [...keywordData].sort((a, b) => {
      const aVal = readValue(a, key);
      const bVal = readValue(b, key);
      if (typeof aVal === 'string') {
        return direction === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }
      return direction === 'asc'
        ? aVal - bVal
        : bVal - aVal;
    });
    setKeywordData(sorted);
  };

  const SortIcon = ({ column }) => {
    if (sortKey !== column) {
      return <IconChevronUp size={14} color="#adb5bd" />;
    }
    return sortDirection === 'asc' ? (
      <IconChevronUp size={14} color="#212529" />
    ) : (
      <IconChevronDown size={14} color="#212529" />
    );
  };

  const SortableTh = ({ column, label }) => (
    <th>
      <UnstyledButton onClick={() => handleSort(column)}>
        <Group spacing={4} align="center" noWrap>
          <Text size="sm" fw={500}>{label}</Text>
          <SortIcon column={column} />
        </Group>
      </UnstyledButton>
    </th>
  );

  /* Totals so the drawer can be checked against the ad row it was opened from. */
  const totals = keywordData.reduce(
    (acc, row) => {
      acc.uniques += Number(row.uniques) || 0;
      acc.visits += Number(row.visits) || 0;
      goals.forEach(goal => {
        if (!acc.goals[goal.key]) {
          acc.goals[goal.key] = { conversions: 0, revenue: 0 };
        }
        acc.goals[goal.key].conversions += Number(row.goals?.[goal.key]?.conversions) || 0;
        acc.goals[goal.key].revenue += Number(row.goals?.[goal.key]?.revenue) || 0;
      });
      return acc;
    },
    { uniques: 0, visits: 0, goals: {} }
  );

  const rate = (part, whole) => (whole > 0 ? ((part / whole) * 100).toFixed(2) : '0.00');

  const exportKeywords = () => {
    const headers = ['Keyword', 'Uniques', 'Visits'];
    goals.forEach(goal => {
      headers.push(`${goal.name} Conversions`, `${goal.name} UCR %`, `${goal.name} CR %`, `${goal.name} Revenue`);
    });
    /* Keywords can contain commas, so every cell is quoted and inner quotes doubled. */
    const cell = (value) => `"${String(value ?? '').replace(/"/g, '""')}"`;
    const rows = keywordData.map(row => {
      const line = [row.keyword, row.uniques, row.visits];
      goals.forEach(goal => {
        const g = row.goals?.[goal.key] || {};
        line.push(g.conversions ?? 0, g.ucr ?? '0.00', g.cr ?? '0.00', g.revenue ?? 0);
      });
      return line;
    });
    const totalLine = ['Total', totals.uniques, totals.visits];
    goals.forEach(goal => {
      const g = totals.goals[goal.key] || { conversions: 0, revenue: 0 };
      totalLine.push(
        g.conversions,
        rate(g.conversions, totals.uniques),
        rate(g.conversions, totals.visits),
        g.revenue.toFixed(2)
      );
    });
    const csvContent = [headers, ...rows, totalLine]
      .map(line => line.map(cell).join(','))
      .join('\n');
    const safeAdName = (selectedAdName || 'ad')
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
    const fileName = `ad-keywords-${safeAdName}.csv`;

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const columnCount = 3 + goals.length * 4;

  return (
    <Drawer
      opened={showAdKeywordDrawer}
      onClose={() => setShowAdKeywordDrawer(false)}
      position="right"
      size="xl"
      withCloseButton={false}
    >
      <Drawer.Header>
        <Group w="100%" position="apart" align="center">
          <Drawer.Title>
            Keyword View – <Text span fw={600}>{selectedAdName}</Text>
          </Drawer.Title>
          <Group spacing="xs">
            <Button size="sm" variant="light" onClick={exportKeywords}>
              Export
            </Button>
            <Drawer.CloseButton />
          </Group>
        </Group>
      </Drawer.Header>
      {isVisiLoaderVisible ? (
        <VisiLoader VisiLoaderVisible={isVisiLoaderVisible}/>
      ) : (
        <>
          <Text size="xs" color="dimmed" mb="xs">
            Keywords that drove traffic through this ad, with Visiopt results. Google reports spend
            per ad group only, so no spend is shown per keyword here.
          </Text>
          <div style={{ overflowX: 'auto' }}>
            <Table striped>
              <thead>
                <tr>
                  <SortableTh column="keyword" label="Keyword" />
                  <SortableTh column="uniques" label="Uniques" />
                  <SortableTh column="visits" label="Visits" />
                  {goals.map(goal => (
                    <React.Fragment key={goal.key}>
                      <SortableTh column={`goal:${goal.key}:conversions`} label={`${goal.name} Conv.`} />
                      <SortableTh column={`goal:${goal.key}:ucr`} label={`${goal.name} UCR %`} />
                      <SortableTh column={`goal:${goal.key}:cr`} label={`${goal.name} CR %`} />
                      <SortableTh column={`goal:${goal.key}:revenue`} label={`${goal.name} Revenue`} />
                    </React.Fragment>
                  ))}
                </tr>
              </thead>
              <tbody>
                {keywordData.length > 0 ? (
                  keywordData.map((row, index) => (
                    <tr key={`${row.keyword}-${index}`}>
                      <td>{row.keyword}</td>
                      <td>{Number(row.uniques).toLocaleString()}</td>
                      <td>{Number(row.visits).toLocaleString()}</td>
                      {goals.map(goal => {
                        const g = row.goals?.[goal.key] || {};
                        return (
                          <React.Fragment key={goal.key}>
                            <td>{Number(g.conversions || 0).toLocaleString()}</td>
                            <td>{g.ucr ?? '0.00'}%</td>
                            <td>{g.cr ?? '0.00'}%</td>
                            <td>${Number(g.revenue || 0).toFixed(2)}</td>
                          </React.Fragment>
                        );
                      })}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={columnCount} style={{ textAlign: 'center' }}>
                      No keywords found for this ad
                    </td>
                  </tr>
                )}
              </tbody>
              {keywordData.length > 0 && (
                <tfoot>
                  <tr style={{ borderTop: '2px solid #dee2e6' }}>
                    <td><Text size="sm" fw={700}>Total</Text></td>
                    <td><Text size="sm" fw={700}>{totals.uniques.toLocaleString()}</Text></td>
                    <td><Text size="sm" fw={700}>{totals.visits.toLocaleString()}</Text></td>
                    {goals.map(goal => {
                      const g = totals.goals[goal.key] || { conversions: 0, revenue: 0 };
                      return (
                        <React.Fragment key={goal.key}>
                          <td><Text size="sm" fw={700}>{g.conversions.toLocaleString()}</Text></td>
                          <td><Text size="sm" fw={700}>{rate(g.conversions, totals.uniques)}%</Text></td>
                          <td><Text size="sm" fw={700}>{rate(g.conversions, totals.visits)}%</Text></td>
                          <td><Text size="sm" fw={700}>${g.revenue.toFixed(2)}</Text></td>
                        </React.Fragment>
                      );
                    })}
                  </tr>
                </tfoot>
              )}
            </Table>
          </div>
        </>
      )}
    </Drawer>
  );
}
